import { useMemo, useRef, useState } from 'react';
import { mutate } from 'swr';
import { toast } from 'sonner';
import { CloudUpload, Download, Search } from 'lucide-react';
import { get, post, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { parseCsv, rowsToProducts, type CsvRowResult } from '@/lib/csv';
import { CATEGORIES, TIERS, type ProductConfig } from '@/lib/product-config';

// Native Bulk Import — replaces the link-cards to product-config-admin /
// productconfig-dashboard. Two paths into productConfigs:
//   · From supplier: style search (S&S / SanMar) → pick colors → configs
//   · From CSV: the products-export format, round-trip safe
type BulkResult = { success?: boolean; partial?: boolean; created?: number; updated?: number; errors?: number; failedIds?: (string | null)[]; total?: number };

type SupplierColor = { name?: string; hex?: string; imageUrl?: string; images?: { front?: string; back?: string; side?: string } };
type SupplierProduct = {
  styleNumber?: string;
  productName?: string;
  brand?: string;
  description?: string;
  colors?: SupplierColor[];
  sizes?: string[];
  fabricComposition?: string;
};

function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string; invalid?: { index: number; error: string }[] } | null;
  if (body?.invalid?.length) return `${body.invalid.length} invalid rows (first: row ${body.invalid[0].index + 1} — ${body.invalid[0].error})`;
  return body?.message || body?.error || fallback;
}

function colorSlug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function BulkImport() {
  const [tab, setTab] = useState<'supplier' | 'csv'>('supplier');

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Bulk Import"
        subtitle="Create product configs in bulk — from a supplier style lookup or a CSV in the products-export format"
        actions={
          <Button variant="secondary" asChild>
            <a href="/api/products-export">
              <Download className="h-4 w-4" aria-hidden /> Export current catalog
            </a>
          </Button>
        }
      />

      <div className="mb-5 flex gap-1.5">
        {([['supplier', 'From supplier'], ['csv', 'From CSV']] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            className={`rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-colors ${tab === v ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'supplier' ? <SupplierImport /> : <CsvImport />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SupplierImport() {
  const [supplier, setSupplier] = useState<'ss' | 'sanmar'>('ss');
  const [style, setStyle] = useState('');
  const [searching, setSearching] = useState(false);
  const [product, setProduct] = useState<SupplierProduct | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState('tees');
  const [tier, setTier] = useState('essentials');
  const [basePrice, setBasePrice] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  async function search() {
    if (!style.trim()) return;
    setSearching(true);
    setProduct(null);
    setResult(null);
    try {
      const r = await get<{ success?: boolean; product?: SupplierProduct; error?: string }>(
        `/api/supplier/search?supplier=${supplier}&style=${encodeURIComponent(style.trim())}`
      );
      if (!r.product) throw new ApiError(404, { message: r.error || 'Style not found' });
      setProduct(r.product);
      setPicked(new Set((r.product.colors || []).map((c) => c.name || '').filter(Boolean)));
    } catch (e) {
      toast.error(errMsg(e, 'Search failed'));
    } finally {
      setSearching(false);
    }
  }

  const rows: ProductConfig[] = useMemo(() => {
    if (!product) return [];
    return (product.colors || [])
      .filter((c) => c.name && picked.has(c.name))
      .map((c) => ({
        id: `ts-${colorSlug(c.name!)}-${String(product.styleNumber || style).toLowerCase()}-lc`,
        productTitle: product.productName,
        displayName: `${c.name} ${product.productName || product.styleNumber}`,
        category,
        tier,
        brandPartner: product.brand,
        gender: 'unisex',
        active: true,
        baseProduct: {
          styleNumber: product.styleNumber || style.trim(),
          color: c.name,
          supplier: supplier === 'ss' ? 'S&S Activewear' : 'SanMar',
          brand: product.brand,
          fabricComposition: product.fabricComposition,
        },
        sizes: product.sizes,
        pricing: { basePrice: basePrice.trim() === '' ? undefined : Number(basePrice), currency: 'USD' },
        shopify: { vendor: 'Ghost Fit Apparel', productType: category, tags: ['team-store', String(product.styleNumber || style).toLowerCase(), colorSlug(c.name!)] },
        views: {
          front: { imageUrl: c.images?.front || c.imageUrl || undefined },
          back: { imageUrl: c.images?.back || undefined },
          side: { imageUrl: c.images?.side || undefined },
        },
      }));
  }, [product, picked, category, tier, basePrice, supplier, style]);

  async function doImport() {
    setImporting(true);
    setResult(null);
    try {
      const r = await post<BulkResult>('/api/product-configs/bulk', { products: rows });
      setResult(r);
      toast[r.partial ? 'warning' : 'success'](`Imported — ${r.created ?? 0} created, ${r.updated ?? 0} updated${r.errors ? `, ${r.errors} failed` : ''}`);
      await mutate('/api/products?limit=500');
    } catch (e) {
      toast.error(errMsg(e, 'Import failed'));
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-2 rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Supplier</label>
          <select className="h-8 rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px]" value={supplier} onChange={(e) => setSupplier(e.target.value as 'ss' | 'sanmar')}>
            <option value="ss">S&S Activewear</option>
            <option value="sanmar">SanMar</option>
          </select>
        </div>
        <div className="min-w-[200px] flex-1">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Style number</label>
          <Input value={style} onChange={(e) => setStyle(e.target.value)} placeholder="PC78H" onKeyDown={(e) => e.key === 'Enter' && search()} />
        </div>
        <Button disabled={searching || !style.trim()} onClick={search}>
          <Search className="h-4 w-4" aria-hidden /> {searching ? 'Searching…' : 'Look up'}
        </Button>
      </div>

      {product && (
        <>
          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <h3 className="text-[14px] font-semibold tracking-tight">{product.productName || product.styleNumber}</h3>
              <span className="text-[11.5px] text-muted">{product.brand} · {(product.sizes || []).join(', ')}</span>
            </div>
            {product.fabricComposition && <p className="mb-3 text-[11.5px] text-muted">{product.fabricComposition}</p>}

            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-[10.5px] uppercase tracking-wide text-muted">Category</span>
                <select className="h-8 w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px]" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-[10.5px] uppercase tracking-wide text-muted">Tier</span>
                <select className="h-8 w-full rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px]" value={tier} onChange={(e) => setTier(e.target.value)}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-[10.5px] uppercase tracking-wide text-muted">Base price ($, all rows)</span>
                <Input type="number" step="0.01" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} placeholder="set later" />
              </label>
              <div className="flex items-end gap-2 pb-px text-[11.5px] text-muted">
                {picked.size}/{(product.colors || []).length} colors selected
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(product.colors || []).map((c) => {
                const name = c.name || '';
                const on = picked.has(name);
                const img = c.images?.front || c.imageUrl;
                return (
                  <button
                    key={name}
                    onClick={() => setPicked((p) => { const n = new Set(p); if (on) n.delete(name); else n.add(name); return n; })}
                    className={`flex items-center gap-2.5 rounded-[10px] border p-2 text-left transition-all ${on ? 'border-primary/50 bg-primary/[0.04]' : 'border-black/[0.07] opacity-60 hover:opacity-100'}`}
                  >
                    {img ? (
                      <img src={img} alt={name} className="h-10 w-10 rounded-[6px] bg-bg object-contain" loading="lazy" />
                    ) : (
                      <span className="h-10 w-10 rounded-[6px] border border-black/[0.06]" style={{ background: c.hex || '#eee' }} />
                    )}
                    <span className="flex-1 truncate text-[12px] font-medium">{name}</span>
                    <input type="checkbox" readOnly checked={on} className="accent-[#007aff]" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button size="lg" disabled={rows.length === 0 || importing} onClick={doImport}>
              <CloudUpload className="h-4 w-4" aria-hidden /> {importing ? 'Importing…' : `Import ${rows.length} config${rows.length === 1 ? '' : 's'}`}
            </Button>
            <span className="text-[11.5px] text-muted">Existing ids are updated, new ids created. Pricing/zones can be refined in Products after import.</span>
          </div>
        </>
      )}

      {result && <ResultSummary result={result} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CsvImport() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<CsvRowResult[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  function loadFile(file: File) {
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const grid = parseCsv(String(reader.result || ''));
      if (grid.length < 2) {
        toast.error('CSV needs a header row plus at least one data row.');
        return;
      }
      setFileName(file.name);
      setRows(rowsToProducts(grid[0], grid.slice(1)));
    };
    reader.readAsText(file);
  }

  const good = rows.filter((r) => r.product);
  const bad = rows.filter((r) => r.error);

  async function doImport() {
    setImporting(true);
    setResult(null);
    try {
      const r = await post<BulkResult>('/api/product-configs/bulk', { products: good.map((g) => g.product) });
      setResult(r);
      toast[r.partial ? 'warning' : 'success'](`Imported — ${r.created ?? 0} created, ${r.updated ?? 0} updated${r.errors ? `, ${r.errors} failed` : ''}`);
      await mutate('/api/products?limit=500');
    } catch (e) {
      toast.error(errMsg(e, 'Import failed'));
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); e.target.value = ''; }} />
      <button
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) loadFile(f); }}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-[14px] border-2 border-dashed border-black/[0.1] bg-surface/60 py-12 text-muted transition-colors hover:border-primary/40 hover:bg-primary/[0.03] hover:text-primary"
      >
        <CloudUpload className="h-7 w-7" aria-hidden />
        <span className="text-[13px] font-medium">{fileName || 'Drop a CSV here, or click to browse'}</span>
        <span className="text-[10.5px]">Same columns as “Export current catalog” — Product ID + Style Number required</span>
      </button>

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-success/10 text-success">{good.length} ready</Badge>
            {bad.length > 0 && <Badge className="bg-danger/10 text-danger">{bad.length} skipped</Badge>}
            <Button disabled={good.length === 0 || importing} onClick={doImport}>
              {importing ? 'Importing…' : `Import ${good.length} row${good.length === 1 ? '' : 's'}`}
            </Button>
          </div>

          <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-[11.5px]">
                <thead className="sticky top-0 bg-bg/95 text-left text-[10px] uppercase tracking-[0.05em] text-muted backdrop-blur">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">ID</th>
                    <th className="px-3 py-2">Display name</th>
                    <th className="px-3 py-2">Style</th>
                    <th className="px-3 py-2">Color</th>
                    <th className="px-3 py-2">Price</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.index} className="border-t border-divider">
                      <td className="px-3 py-1.5 font-mono text-[10px] text-subtle">{r.index + 1}</td>
                      <td className="max-w-[180px] truncate px-3 py-1.5 font-mono text-[10px]">{r.product?.id || '—'}</td>
                      <td className="max-w-[200px] truncate px-3 py-1.5">{r.product?.displayName || '—'}</td>
                      <td className="px-3 py-1.5">{r.product?.baseProduct?.styleNumber || '—'}</td>
                      <td className="px-3 py-1.5">{r.product?.baseProduct?.color || '—'}</td>
                      <td className="px-3 py-1.5">{r.product?.pricing?.basePrice != null ? `$${r.product.pricing.basePrice}` : '—'}</td>
                      <td className="px-3 py-1.5">
                        {r.error
                          ? <Badge className="bg-danger/10 text-danger" title={r.error}>{r.error}</Badge>
                          : <Badge className="bg-success/10 text-success">ok</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {result && <ResultSummary result={result} />}
    </div>
  );
}

function ResultSummary({ result }: { result: BulkResult }) {
  const failed = (result.failedIds || []).filter(Boolean) as string[];
  return (
    <div className={`rounded-[12px] p-4 text-[12px] ${result.partial ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'}`}>
      <span className="font-semibold">{result.partial ? 'Partial import.' : 'Import complete.'}</span>{' '}
      {result.created ?? 0} created · {result.updated ?? 0} updated · {result.errors ?? 0} failed
      {failed.length > 0 && <span className="block pt-1 font-mono text-[10.5px]">failed: {failed.join(', ')}</span>}
    </div>
  );
}
